import React, { useState, useEffect, useCallback } from 'react';
import { get, debounce } from 'lodash';
import { useViewerStore, actions } from '@store/viewerModule/base';
import Empty from '@components/viewer-sessions/table/Empty';
import { sortAndSearchTable, replaceFinalDataKey } from './utils';
import ColumnDropMenu from './ColumnsDropMenu';
import {
  ViewerSessionsTableHeader,
  DataTriggerBoxWrapper,
  DataTableWrapper,
  DataTriggerBox,
} from './styled';
import Table from '@src/components/common/Table';
import useFetchData from './useFetchData';
import LoadingFooter from './LoadingFooter';

function SubscriberTable({ sessionData }) {
  const [selectedRowIndex, setRowIndex] = useState(-1);
  const [selectRowIndex, setSelectRowIndex] = useState();
  const [isSelected, setIsSelected] = useState(false);
  const [tableData, setTableData] = useState<any>([]);
  const [sortBy, setSortBy] = useState({});
  const { fetchNextPage, isFetchingNextPage } = useFetchData();
  const [{ searchSortData, tableColumns }, dispatchContext] = useViewerStore();

  const headerRender = useCallback(
    ({ cells, columns: tableColumns }) =>
      tableColumns.map((item, index) => {
        return (
          <ViewerSessionsTableHeader key={item.title} title={item.title}>
            {cells[index]}
          </ViewerSessionsTableHeader>
        );
      }),
    [],
  );

  const columnSort = (data) => {
    const {
      key,
      order: newOrder,
      column: { dataKey },
    } = data;
    dispatchContext(
      actions.setSearchSortData({
        order: newOrder,
        orderKey: key,
        columnOrderKey: dataKey,
      }),
    );
  };

  useEffect(() => {
    const { columnKey, order, columnOrderKey } = searchSortData;
    const { key = '' } = tableColumns
      ? tableColumns.find((item) => item.dataKey === columnOrderKey) || {}
      : {};
    setSortBy({
      order,
      key,
    });
    const replacedData = sessionData ? replaceFinalDataKey(sessionData) : [];
    const sortedData = sortAndSearchTable(columnKey, '', order, replacedData, columnOrderKey);
    setTableData(sortedData);
  }, [sessionData, tableColumns, searchSortData]);

  const onRowClick = (row) => {
    const { rowData, rowIndex } = row;
    setSelectRowIndex(rowIndex);
    if (rowIndex !== selectedRowIndex && !isSelected) {
      setRowIndex(rowIndex);
      setIsSelected(true);
    } else {
      setRowIndex(-1);
      setIsSelected(false);
    }
    const geoData: any = {
      startTime: rowData.SessionStartTime,
      encodedGeo: rowData.location ? rowData.location.encodedGeo : '',
      asn: rowData.location ? rowData.location.asn : '',
      city: rowData.location ? rowData.location.cityCode : '',
    };
    dispatchContext(actions.setAdvanced({ showAdvanceDetails: true }));
    dispatchContext(actions.setRowData({ rowData }));
    dispatchContext(actions.setGeoData({ geoData }));
  };

  const rowEventHandlers = {
    onClick: onRowClick,
  };

  const rowClassName = (row) => {
    const { rowIndex } = row;
    const isCurrentRowSelected = rowIndex === selectRowIndex;
    const className = isCurrentRowSelected ? 'row-selected' : '';
    return className;
  };

  const handleEnd = debounce(() => {
    fetchNextPage();
  }, 300);

  return (
    <>
      <DataTriggerBoxWrapper>
        <DataTriggerBox pushRight={false}>
          <ColumnDropMenu />
        </DataTriggerBox>
      </DataTriggerBoxWrapper>
      <DataTableWrapper>
        <Table
          data={tableData}
          columns={tableColumns}
          headerHeight={45}
          rowHeight={45}
          fixed
          headerRenderer={headerRender}
          rowEventHandlers={rowEventHandlers}
          rowClassName={rowClassName}
          sortBy={sortBy}
          onColumnSort={columnSort}
          emptyRenderer={<Empty />}
          onEndReachedThreshold={100}
          onEndReached={handleEnd}
          footerHeight={isFetchingNextPage ? 25 : 0}
          footerRenderer={isFetchingNextPage ? LoadingFooter : null}
        />
      </DataTableWrapper>
    </>
  );
}

export default React.memo(SubscriberTable);
